<template>
  <div class="activity-settings-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain style="margin-right: 10px;">
            返回首页
          </el-button>
          <span>活动内容设置</span>
          <el-button type="primary" @click="handleAdd">添加活动</el-button>
        </div>
      </template>
      
      <el-table :data="activityList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="activity_name" label="活动名称" width="180" />
        <el-table-column prop="description" label="活动描述" show-overflow-tooltip />
        <el-table-column prop="start_time" label="开始时间" width="180" />
        <el-table-column prop="end_time" label="结束时间" width="180" />
        <el-table-column prop="location" label="活动地点" width="180" />
        <el-table-column prop="max_participants" label="最大参与人数" width="120">
          <template #default="scope">
            {{ scope.row.max_participants === 0 ? '不限' : scope.row.max_participants }}
          </template>
        </el-table-column>
        <el-table-column prop="cur_participants" label="当前参与人数" width="120">
          <template #default="scope">
            {{ scope.row.cur_participants || 0 }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'danger'">
              {{ Number(scope.row.status) === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              :type="Number(scope.row.status) === 1 ? 'danger' : 'success'" 
              size="small" 
              @click="handleStatusChange(scope.row)"
            >
              {{ Number(scope.row.status) === 1 ? '禁用' : '启用' }}
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加活动' : '编辑活动'" 
      v-model="dialogVisible"
      width="600px"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="活动名称" prop="activity_name">
          <el-input v-model="form.activity_name" />
        </el-form-item>
        <el-form-item label="活动描述" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="开始时间" prop="start_time">
          <el-date-picker
            v-model="form.start_time"
            type="datetime"
            placeholder="选择开始时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
        <el-form-item label="结束时间" prop="end_time">
          <el-date-picker
            v-model="form.end_time"
            type="datetime"
            placeholder="选择结束时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
        <el-form-item label="活动地点" prop="location">
          <el-input v-model="form.location" />
        </el-form-item>
        <el-form-item label="最大参与人数" prop="max_participants">
          <el-input-number v-model="form.max_participants" :min="0" />
          <span class="tip">0表示不限制人数</span>
        </el-form-item>
        <el-form-item label="当前参与人数">
          <el-input v-model="form.cur_participants" disabled />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'ActivitySettings',
  setup() {
    const loading = ref(false)
    const activityList = ref([])
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    
    const form = ref({
      activity_name: '',
      description: '',
      start_time: '',
      end_time: '',
      location: '',
      max_participants: 0,
      cur_participants: 0,  // 添加当前参与人数字段
      status: 1
    })
    
    const rules = {
      activity_name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
      description: [{ required: true, message: '请输入活动描述', trigger: 'blur' }],
      start_time: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
      end_time: [{ required: true, message: '请选择结束时间', trigger: 'change' }],
      location: [{ required: true, message: '请输入活动地点', trigger: 'blur' }]
    }
    
    const fetchActivityList = async () => {
      loading.value = true
      try {
        const response = await fetch(`/api/activity_settings_list.php?page=${currentPage.value}&pageSize=${pageSize.value}`)
        const data = await response.json()
        if (data.code === 0) {
          activityList.value = data.data.list
          total.value = data.data.total
        } else {
          ElMessage.error(data.msg || '获取活动列表失败')
        }
      } catch (error) {
        console.error('获取活动列表失败:', error)
        ElMessage.error('获取活动列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleAdd = () => {
      form.value = {
        activity_name: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        max_participants: 0,
        cur_participants: 0,  // 添加当前参与人数默认值
        status: 1
      }
      dialogType.value = 'add'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      form.value = { ...row }
      dialogType.value = 'edit'
      dialogVisible.value = true
    }
    
    const handleStatusChange = async (row) => {
      try {
        const action = Number(row.status) === 1 ? '禁用' : '启用'
        await ElMessageBox.confirm(`确定要${action}该活动吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/activity_settings_status.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            status: Number(row.status) === 1 ? 0 : 1
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(`${action}成功`)
          fetchActivityList()
        } else {
          ElMessage.error(data.msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
          ElMessage.error('更新状态失败')
        }
      }
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除该活动吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/activity_settings_delete.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success('删除成功')
          fetchActivityList()
        } else {
          ElMessage.error(data.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        const url = dialogType.value === 'add' ? '/api/activity_settings_add.php' : '/api/activity_settings_edit.php'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form.value)
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(dialogType.value === 'add' ? '添加成功' : '更新成功')
          dialogVisible.value = false
          fetchActivityList()
        } else {
          ElMessage.error(data.msg || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
        }
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error('表单验证失败')
      }
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchActivityList()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchActivityList()
    }
    
    onMounted(() => {
      fetchActivityList()
    })
    
    return {
      loading,
      activityList,
      dialogVisible,
      dialogType,
      form,
      rules,
      formRef,
      currentPage,
      pageSize,
      total,
      handleAdd,
      handleEdit,
      handleStatusChange,
      handleDelete,
      handleSubmit,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.activity-settings-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

:deep(.el-date-editor.el-input) {
  width: 100%;
}
</style> 